<template>
  <div>
    <v-btn
      depressed
      x-small
      :to="{ name: 'works.review', params: { bookingId, bikeId, apiSignature }, query: { customerFirstName: job.booking.customer.first_name } }"
    >
      <v-icon x-small left>mdi-arrow-left-circle</v-icon>
      Back to Review
    </v-btn>

    <v-row justify="center" class="pt-6">
      <v-col
        cols="12"
        sm="5"
        lg="4"
      >
        <v-card outlined>
          <v-card-text>
            <h4 class="mb-4 primary--text">Extra Work Confirmed</h4>
            <p>Thank you for confirming the additional work reccomended by our technician. Please use the secure payment form to complete the approval, you can use the payment method you used for booking, or enter a new method of payment.</p>

            <h4 class="mb-4 mt-6">What Happens Next?</h4>
            <p>Once payment has been taken, your bike will make it's way into the workshop! If there are parts that need to be ordered, we will wait until they arrive to complete the service.</p>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="5"
        lg="4"
        xl="3"
      >
        <v-card outlined>
          <v-list-item two-line>
            <v-list-item-content>
              <v-list-item-title>
                <h2>Secure Card Payment</h2>
              </v-list-item-title>
              <v-list-item-subtitle>
                <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-visa']" />
                <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-mastercard']" />
                <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-jcb']" />
                <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-discover']" />
                <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-diners-club']" />
                <font-awesome-icon class="mr-1" size="lg" :icon="['fab', 'cc-amex']" />
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-divider class="mt-2 mb-4" />

          <v-card-text>
            <v-select
              v-model="form.payment_method"
              :items="paymentMethods"
              :disabled="!paymentIntent.amount"
              label="Choose Payment Method"
              item-value="id"
              outlined
            >
              <template v-slot:item="{ item: paymentMethod }">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{ paymentMethod.name || paymentMethod.card_brand.toUpperCase() }} {{ paymentMethod.card_exp_last4 }}</strong>
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="!paymentMethod.description" class="mt-2"><small>Exp {{ paymentMethod.card_exp_month }} / {{ paymentMethod.card_exp_year }}</small></v-list-item-subtitle>
                  <v-list-item-subtitle v-else class="mt-2"><small>{{ paymentMethod.description }}</small></v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-list-item-action-text>
                    <font-awesome-icon v-if="paymentMethod.card_brand" class="mr-1" size="lg" :icon="['fab', `cc-${paymentMethod.card_brand}`]" />
                    <v-icon v-else-if="paymentMethod.icon" small>mdi-{{ paymentMethod.icon }}</v-icon>
                  </v-list-item-action-text>
                </v-list-item-action>
              </template>
              <template v-slot:selection="{ item: paymentMethod }">
                <v-list-item-content>
                  <v-list-item-title>
                    <font-awesome-icon v-if="paymentMethod.card_brand" class="mr-2" :icon="['fab', `cc-${paymentMethod.card_brand}`]" />
                    <strong>{{ paymentMethod.name || paymentMethod.card_brand.toUpperCase() }} {{ paymentMethod.card_exp_last4 }}</strong>
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="!paymentMethod.description" class="mt-2"><small>Exp {{ paymentMethod.card_exp_month }} / {{ paymentMethod.card_exp_year }}</small></v-list-item-subtitle>
                  <v-list-item-subtitle v-else class="mt-2"><small>{{ paymentMethod.description }}</small></v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>

            <div v-if="chosenPaymentMethod">
              <p>We have a card saved on file. For security, please enter CVC for {{ chosenPaymentMethod.card_brand.toUpperCase() }} ending {{ chosenPaymentMethod.card_exp_last4 }}</p>
            </div>

            <v-row v-if="chosenPaymentMethod">
              <v-col sm="5" md="4" lg="3" cols="4">
                <div
                  class="card_input"
                  id="cvc-element"
                />
                <div
                  class="v-messages theme--light error--text"
                  role="alert"
                >
                  <div
                    class="v-messages__wrapper"
                  >
                    <div
                      v-if="paymentElementErrorMessage"
                      class="v-messages__message"
                    >
                      {{ paymentElementErrorMessage }}
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col>
                <v-icon class="mt-1">mdi-credit-card-settings</v-icon>
              </v-col>
            </v-row>

            <template v-else-if="form.payment_method === 'new_method'">
              <v-row>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="form.cardholder_name"
                    :rules="[v => !!v || 'Cardholder name is required']"
                    label="Cardholder Name"
                    outlined
                    required
                    dense
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="form.billing_postcode"
                    :rules="[v => !!v || 'Billing postcode is required']"
                    label="Billing Postcode"
                    outlined
                    required
                    dense
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <div
                    class="card_input"
                    id="card-element"
                  />
                  <div
                    class="v-text-field__details"
                    style="padding-left: 10px; margin-bottom: 8px"
                  >
                    <div
                      class="v-messages theme--light error--text"
                      role="alert"
                    >
                      <div
                        class="v-messages__wrapper"
                      >
                        <div
                          v-if="paymentElementErrorMessage"
                          class="v-messages__message"
                        >
                          {{ paymentElementErrorMessage }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </template>

            <v-alert v-if="paymentError.message" class="mt-4" color="error" dense text outlined>
              {{ paymentError.message }}
            </v-alert>
<!--
            <v-alert v-if="job.balance < 0" class="mt-4" color="info" dense text outlined>
              A refund of {{ $currency(job.balance) }} is due, this may be because of a change in work completed. This will be processed by our team.
            </v-alert> -->

            <v-alert v-if="!paymentIntent.amount && paymentIntent.id" class="mt-4" color="info" dense text outlined>
              There is no payment due! Please complete the approval process by clicking the button below.
            </v-alert>

            <v-btn
              v-if="paymentIntent.amount"
              color="green"
              class="mt-4"
              block
              dark
              depressed
              large
              :loading="loading"
              @click="handlePayment"
            >
              Pay {{ $currency(paymentIntent.amount / 100) }}
            </v-btn>
            <v-btn
              v-else
              color="green"
              class="mt-4"
              block
              dark
              depressed
              large
              :loading="loading"
              @click="completeNoPayment"
            >
              Complete <small class="ml-2">(No Payment Required)</small>
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import HasBookingId from '@/mixins/HasBookingId'
import HasBikeId from '@/mixins/HasBikeId'
import HasPostcode from '@/mixins/HasPostcode'
import HasApiSignature from '@/mixins/HasApiSignature'
import axios from 'axios'
import uniqBy from 'lodash.uniqby'

export default {
  name: 'Pay',

  mixins: [
    HasBookingId,
    HasBikeId,
    HasPostcode,
    HasApiSignature
  ],

  data () {
    return {
      loading: false,
      form: {
        payment_method: null
      },
      paymentElementErrorMessage: null,
      paymentIntent: {},
      paymentError: {},
      stripeIsComplete: false,
      job: {
        booking: {
          customer: {
            payment_methods: []
          }
        }
      },
      error: {},
      stripeInstance: null,
      elementStyle: {
        base: {
          color: 'rgba(0, 0, 0, 0.87)',
          fontSize: '16px',
          fontFamily: '"Roboto"',
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: '#646464'
          }
        }
      },
      cvcElement: null,
      cardElement: null
    }
  },

  mounted () {
    this.getPaymentIntent()

    // eslint-disable-next-line
    this.stripeInstance = Stripe(process.env.VUE_APP_STRIPE_KEY)
    this.elements = this.stripeInstance.elements({ fonts: [{ cssSrc: 'https://fonts.googleapis.com/css?family=Roboto' }] })
  },

  watch: {
    'form.payment_method': function () {
      if (this.paymentIntent.amount) {
        if (!this.chosenPaymentMethod) {
          if (this.cvcElement) {
            this.cvcElement.unmount('#cvc-element')
            this.cvcElement.destroy('#cvc-element')
            this.cvcElement = null
          }

          if (this.form.payment_method === 'new_method') {
            this.cardElement = this.elements.create('card', { hidePostalCode: true, style: this.style })
            this.$nextTick(() => {
              this.cardElement.mount('#card-element')

              this.cardElement.on('change', ($e) => {
                this.stripeIsComplete = $e.complete
                if ($e.error) {
                  this.paymentElementErrorMessage = $e.error.message
                } else {
                  this.paymentElementErrorMessage = null
                }
              })
            })
          }
        } else {
          if (this.cardElement) {
            this.cardElement.unmount('#card-element')
            this.cardElement.destroy('#card-element')
            this.cardElement = null
          }
          this.cvcElement = this.elements.create('cardCvc', { style: this.style })
          this.$nextTick(() => {
            this.cvcElement.mount('#cvc-element')

            this.cvcElement.on('change', ($e) => {
              this.stripeIsComplete = $e.complete
              if ($e.error) {
                this.paymentElementErrorMessage = $e.error.message
              } else {
                this.paymentElementErrorMessage = null
              }
            })
          })
        }
      }
    }
  },

  computed: {
    paymentMethods () {
      return [
        ...uniqBy(this.job.booking.customer.payment_methods, 'card_exp_last4'),
        ...[
          { id: 'new_method', name: 'Enter New Payment Method', description: 'Use a new payment method', icon: 'credit-card-plus' }
        ]
      ]
    },

    chosenPaymentMethod () {
      var method

      if (this.paymentIntent.amount) {
        this.job.booking.customer.payment_methods.forEach(paymentMethod => {
          if (paymentMethod.id === this.form.payment_method) {
            method = paymentMethod
          }
        })
      }

      return method
    }
  },

  methods: {
    completeNoPayment () {
      this.loading = true
      this.paymentError = {}
      const axiosInstance = axios.create()
      axiosInstance.defaults.headers.common['Client-Id'] = process.env.VUE_APP_CLIENT_ID
      axiosInstance.post(this.job.signed_sundry_approval_no_balance.api)
        .then(() => {
          this.$router.push({
            name: 'works.complete',
            params: {
              bookingId: this.bookingId,
              bikeId: this.bikeId,
              postcode: this.postcode,
              apiSignature: this.apiSignature
            }
          })
        })
        .catch(({ response: { data: error } }) => {
          this.paymentError = error
        })
        .finally(() => { this.loading = false })
    },

    handlePayment () {
      this.paymentError = {}
      this.loading = true

      var paymentMethodConfig

      if (this.cvcElement) {
        paymentMethodConfig = {
          payment_method: this.chosenPaymentMethod.provider_id,
          payment_method_options: {
            card: {
              cvc: this.cvcElement
            }
          }
        }
      } else if (this.cardElement) {
        paymentMethodConfig = {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              name: this.form.cardholder_name,
              address: {
                postal_code: this.form.billing_postcode
              }
            }
          },
          setup_future_usage: 'off_session'
        }
      }

      this.stripeInstance.confirmCardPayment(this.paymentIntent.client_secret, paymentMethodConfig)
        .then(result => {
          if (result.error) {
            this.paymentError = result.error
          } else {
            if (result.paymentIntent.status === 'succeeded') {
              this.$router.push({
                name: 'works.complete',
                params: {
                  bookingId: this.bookingId,
                  bikeId: this.bikeId,
                  postcode: this.postcode,
                  apiSignature: this.apiSignature
                }
              })
            }
          }
        })
        .finally(() => { this.loading = false })
    },

    getJob () {
      this.loading = true
      const axiosInstance = axios.create({
        baseURL: process.env.VUE_APP_API_BASE_URL
      })
      axiosInstance.defaults.headers.common['Client-Id'] = process.env.VUE_APP_CLIENT_ID
      axiosInstance.get(`/api/bookings/${this.bookingId}/bikes/${this.bikeId}/signed`, {
        params: {
          collection_postcode: this.postcode.replace(/\s+/g, '').toUpperCase(),
          signature: this.apiSignature
        }
      })
        .then(({ data: { data: job } }) => {
          this.job = job
          this.form.payment_method = this.job.booking.customer.payment_methods[0]?.id
          this.error = {}
        })
        .catch(({ response: { data: error } }) => {
          this.error = error
        })
        .finally(() => { this.loading = false })
    },

    getPaymentIntent () {
      this.loading = true
      const axiosInstance = axios.create({
        baseURL: process.env.VUE_APP_API_BASE_URL
      })
      axiosInstance.defaults.headers.common['Client-Id'] = process.env.VUE_APP_CLIENT_ID
      axiosInstance.post('api/payments', {
        booking_bike_id: this.bikeId
      })
        .then(({ data: paymentIntent }) => {
          this.getJob()
          this.paymentIntent = paymentIntent
          this.error = {}
        })
        .catch(({ response: { data: error } }) => {
          this.error = error
        })
        .finally(() => { this.loading = false })
    }
  }
}
</script>
<style type="text/css">
  .card_input {
    margin-bottom: 5px;
    padding: 10px 10px;
    border-radius: 5px;
    border-width: thin;
    border-style: solid;
    border-color: #bebebe;
  }
  .card_input:hover {
    border-color: black;
  }
  .StripeElement--focus {
    border-color: #00a09b!important;
    border-width: 2px;
    margin-bottom: 3px;
  }
  .StripeElement--invalid {
    border-color: #ff0000!important;
    border-width: 2px!important;
  }
</style>
